/**
 * Prop Studio — the placement controls and readouts for the selected prop:
 * scale, turn, offset, mirror; the three fits as starting values (worked out
 * for the model as turned now); the model's size against the kit box; where
 * it stands against the wall; Save and Reset.
 *
 * Every number here comes from placement.ts, the same arithmetic the viewer
 * draws with, so the readout never disagrees with the picture.
 */

import React from 'react';
import { designerControlStyle as control } from '../designer/DesignerHost';
import type { PropDef } from '../../calculations/staging/kits';
import { Bounds, CopyAxis, FT, MAX_COPIES, PropTransform, Vec3, defaultHang, fitSuggestions, fractionToHang, hangToFraction, modelSizeMetres, placedBoundsFeet, stagedSizeFeet, wallClearanceFeet, MAX_PER_WALL } from './placement';
import { propFrame } from './propFrame';
import { NumberField } from './NumberField';
import type { CandidateState } from './PropViewer';
import { PROP_STUDIO_STRINGS as S } from './strings';

export interface PlacementPanelProps {
  def: PropDef;
  modelBounds: Bounds | null;
  draft: PropTransform;
  dirty: boolean;
  saving: boolean;
  candidate: CandidateState;
  onChange: (patch: Partial<PropTransform>) => void;
  onSave: () => void;
  onReset: () => void;
  /** The model this one is a part of (its label), when it is a part. */
  partOf?: string;
  /** Picking the hang point on the model in the viewer, and the switch for it. */
  picking?: boolean;
  onTogglePick?: () => void;
  /** A fixture (the opener's motor): size, turn and nudge only -- no hang point, no copies. */
  simple?: boolean;
  /** Floor props: set the model's lowest point (its mesh, as drawn) on the floor; absent when the viewer cannot measure it. */
  onRestOnFloor?: () => void;
  /** Set the model's nearest point (its mesh, as drawn) on the wall line; absent when the viewer cannot measure it (the box is used then). */
  onFlushToWall?: () => void;
}

const ACCENT = '#FEB100';
const MUTED = '#9a9ca6';
const WARN = '#ff7b7b';
const LABEL: React.CSSProperties = { fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4 };
const ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6 };
const NUMBER: React.CSSProperties = { ...control, width: 72, padding: '3px 6px', textAlign: 'right' };
const SMALL_BUTTON: React.CSSProperties = { ...control, padding: '2px 7px', cursor: 'pointer', fontSize: 11 };
const PRIMARY_BUTTON: React.CSSProperties = { ...control, background: ACCENT, color: '#000', border: 'none', fontWeight: 700, cursor: 'pointer', padding: '6px 14px' };

const fmt = (n: number, digits = 2): string => (Number.isFinite(n) ? n.toFixed(digits) : '–');
const signed = (n: number): string => (Math.abs(n) < 0.005 ? '0.00' : `${n > 0 ? '+' : ''}${n.toFixed(2)}`);
const cm = (feet: number): string => (feet * FT * 100).toFixed(0);
/** Three decimals is as fine as the inputs go; it also keeps 0.1 + 0.2 from showing as 0.30000000000000004. */
const tidy = (n: number): number => Math.round(n * 1000) / 1000;
/** What the helper will save (0.01–100); the slider covers the useful part of it. */
const scaleAccepted = (v: number): boolean => v >= 0.01 && v <= 100;

/**
 * The hang point moved by hand: for each axis of the model's box, a slider
 * and a typed percentage of the box, and the point itself in feet (the
 * model's own frame: along, deep, tall from its box's corner at 0).
 */
const HangSliders: React.FC<{ bounds: Bounds; hang: Vec3; onChange: (hang: Vec3) => void }> = ({ bounds, hang, onChange }) => {
  const f = hangToFraction(bounds, hang);
  const set = (axis: number, pct: number) => {
    if (!Number.isFinite(pct)) return;
    const next = [...f] as Vec3;
    next[axis] = Math.max(-1, Math.min(2, pct / 100));
    onChange(fractionToHang(bounds, next));
  };
  // Typed in feet from the box's corner (its min on that axis); stored in metres in the model's frame.
  const setFeet = (axis: number, feet: number) => {
    if (!Number.isFinite(feet)) return;
    const next = [...hang] as Vec3;
    next[axis] = Math.round((bounds.min[axis] + feet * FT) * 100000) / 100000;
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }} title={S.hang.percentTitle}>
      {S.hang.axes.map(([label, title], axis) => (
        <div style={ROW} key={label}>
          <span style={{ width: 52, fontSize: 11 }} title={title}>{label}</span>
          <input
            type="range" min={-25} max={125} step={0.5} value={Math.max(-25, Math.min(125, f[axis] * 100))}
            style={{ flex: 1, accentColor: '#4fd6ff' }} aria-label={`${S.hang.title} ${label}`}
            onChange={(e) => set(axis, parseFloat(e.target.value))}
          />
          <NumberField value={tidy(f[axis] * 100)} step={1} style={{ ...NUMBER, width: 58 }} aria-label={`${S.hang.title} ${label} %`} onCommit={(v) => set(axis, v)} />
          <span style={{ fontSize: 11, color: MUTED }}>%</span>
          <NumberField value={tidy((hang[axis] - bounds.min[axis]) / FT)} step={0.05} style={{ ...NUMBER, width: 62 }} aria-label={`${S.hang.title} ${label} ft`} onCommit={(v) => setFeet(axis, v)} />
          <span style={{ fontSize: 11, color: MUTED }}>{S.readout.feet}</span>
        </div>
      ))}
    </div>
  );
};

export const PlacementPanel: React.FC<PlacementPanelProps> = ({ def, modelBounds, draft, dirty, saving, candidate, onChange, onSave, onReset, partOf, picking = false, onTogglePick, simple = false, onRestOnFloor, onFlushToWall }) => {
  const setOffset = (axis: 0 | 1 | 2, value: number) => {
    const offset = [...draft.offset] as Vec3;
    offset[axis] = tidy(value);
    onChange({ offset });
  };
  // The fits are for the model AS TURNED: a bicycle saved at yaw 90 lies along
  // the wall, and that is the footprint the kit box is compared with.
  const fits = modelBounds ? fitSuggestions(modelBounds, def.size, draft) : null;
  const size = modelBounds ? modelSizeMetres(modelBounds) : null;
  const placed = modelBounds ? placedBoundsFeet(modelBounds, draft) : null;
  const frame = propFrame(def);
  const kit = frame.box;
  const extent = (b: Bounds): Vec3 => [b.max[0] - b.min[0], b.max[1] - b.min[1], b.max[2] - b.min[2]];
  const kitExtent = extent(kit);
  const placedExtent = placed ? extent(placed) : null;
  // Where the model stands against the wall line z = −d/2: behind it is the
  // one placement fault the sizes cannot show, and the render would cut it off.
  const clearance = placed ? wallClearanceFeet(placed, frame.wallZ) : null;
  const behindWall = clearance !== null && clearance < -1e-6;
  // What the designer stages the model at when no stage box is set: the placed size, as tall as the model's top.
  const stagedDefault = placed ? stagedSizeFeet(placed, {}) : null;
  const tidyBox = (b: { w: number; d: number; h: number }) => ({ w: tidy(b.w), d: tidy(b.d), h: tidy(b.h) });
  // The preview file's own size against the recorded bounds: a stale or
  // mismatched conversion shows up here before it shows up in a render.
  const previewDiffers = candidate.phase === 'ready' && size
    ? candidate.sizeMetres.some((v, i) => Math.abs(v - size[i]) > 0.01)
    : false;

  // Offsets: a slider over ±10 ft (the useful range; the field takes any number), with the nudges.
  const offsetRow = (label: string, axis: 0 | 1 | 2) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }} key={axis}>
      <div style={ROW}>
        <span style={{ width: 70, fontSize: 12 }}>{label}</span>
        <button type="button" style={SMALL_BUTTON} onClick={() => setOffset(axis, draft.offset[axis] - 1)} title="−1 ft">−1</button>
        <button type="button" style={SMALL_BUTTON} onClick={() => setOffset(axis, draft.offset[axis] - 0.1)} title="−0.1 ft">−.1</button>
        <NumberField value={draft.offset[axis]} step={0.1} style={NUMBER} aria-label={label} onCommit={(v) => setOffset(axis, v)} />
        <button type="button" style={SMALL_BUTTON} onClick={() => setOffset(axis, draft.offset[axis] + 0.1)} title="+0.1 ft">+.1</button>
        <button type="button" style={SMALL_BUTTON} onClick={() => setOffset(axis, draft.offset[axis] + 1)} title="+1 ft">+1</button>
      </div>
      <div style={{ ...ROW, paddingLeft: 76 }}>
        <input
          type="range" min={-10} max={10} step={0.05} value={Math.min(10, Math.max(-10, draft.offset[axis]))} style={{ flex: 1, accentColor: ACCENT }} aria-label={`${label} slider`}
          onChange={(e) => setOffset(axis, parseFloat(e.target.value))}
        />
      </div>
    </div>
  );

  const sizeRow = (label: string, values: Vec3 | null, unit: string, render: (v: number) => string = (v) => fmt(v)) => (
    <tr>
      <td style={{ color: MUTED, paddingRight: 8, whiteSpace: 'nowrap' }}>{label}</td>
      {[0, 1, 2].map((i) => <td key={i} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', paddingLeft: 10 }}>{values ? render(values[i]) : '–'}</td>)}
      <td style={{ color: MUTED, paddingLeft: 6 }}>{unit}</td>
    </tr>
  );

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ ...ROW, justifyContent: 'space-between' }}>
        <span style={LABEL}>{S.placement.title}</span>
        {dirty && <span style={{ fontSize: 11, color: ACCENT }}>{S.placement.unsaved}</span>}
      </div>
      {!modelBounds && <p style={{ margin: 0, fontSize: 12, color: MUTED }}>{S.placement.noModel}</p>}
      {/* Nothing to place yet: the controls below would place nothing, so they wait for a model. */}
      {modelBounds && (<>
      {partOf
        ? <p style={{ margin: 0, fontSize: 11, color: MUTED }}>{S.parts.note(partOf)}</p>
        : frame.kind === 'held' && (
          <p style={{ margin: 0, fontSize: 11, color: MUTED }}>
            {draft.hang ? S.hang.heldPicked : frame.anchor === 'top' ? S.placement.heldTop(frame.holdZ.toFixed(2)) : S.placement.heldBottom(frame.holdZ.toFixed(2))}
          </p>
        )}

      {/* A held prop's hang point: the box's top- (or bottom-) centre until a spot is picked on the model. */}
      {frame.kind === 'held' && !partOf && !simple && modelBounds && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 10px', border: `1px solid ${picking ? '#4fd6ff' : 'rgba(255,255,255,0.12)'}`, borderRadius: 8 }}>
          <div style={{ ...ROW, justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12 }}>{S.hang.title}</span>
            <span style={{ width: 10, height: 10, borderRadius: 5, border: '2px solid #4fd6ff' }} aria-hidden />
          </div>
          <span style={{ fontSize: 11, color: MUTED }} aria-label={S.hang.title}>
            {draft.hang
              ? S.hang.picked2((draft.hang[0] * 100).toFixed(1), (draft.hang[1] * 100).toFixed(1), (draft.hang[2] * 100).toFixed(1))
              : frame.anchor === 'top' ? S.hang.boxTop : S.hang.boxBottom}
          </span>
          <HangSliders bounds={modelBounds} hang={draft.hang ?? defaultHang(modelBounds, frame.anchor)} onChange={(hang) => onChange({ hang })} />
          <div style={ROW}>
            <button
              type="button" aria-pressed={picking} title={S.hang.pickTitle} onClick={onTogglePick}
              style={{ ...SMALL_BUTTON, ...(picking ? { background: 'rgba(79,214,255,0.18)', border: '1px solid #4fd6ff' } : {}) }}
            >
              {picking ? S.hang.cancel : S.hang.pick}
            </button>
            {draft.hang && !picking && (
              <button type="button" style={SMALL_BUTTON} title={S.hang.clearTitle} onClick={() => onChange({ hang: undefined })}>{S.hang.clear}</button>
            )}
          </div>
          <span style={{ fontSize: 11, color: picking ? '#4fd6ff' : MUTED }}>{picking ? S.hang.picking : `${S.hang.slidersNote} ${S.hang.note}`}</span>
        </div>
      )}

      <div style={ROW}>
        <span style={{ width: 70, fontSize: 12 }}>{S.placement.scale}</span>
        <NumberField value={draft.scale} step={0.01} min={0.01} max={100} accept={scaleAccepted} style={NUMBER} aria-label={S.placement.scale} onCommit={(v) => onChange({ scale: v })} />
        <input
          type="range" min={0.05} max={5} step={0.01} value={Math.min(5, Math.max(0.05, draft.scale))} style={{ flex: 1, accentColor: ACCENT }} aria-label={`${S.placement.scale} slider`}
          onChange={(e) => onChange({ scale: parseFloat(e.target.value) })}
        />
      </div>
      {fits && (
        <div style={ROW}>
          <span style={{ width: 70, fontSize: 11, color: MUTED, whiteSpace: 'nowrap' }} title={S.placement.fitAtTitle}>{S.placement.fit} {S.placement.fitAt(tidy(draft.yaw))}</span>
          <button type="button" style={SMALL_BUTTON} title={S.placement.fitFootprintTitle} onClick={() => onChange({ scale: tidy(fits.footprint) })}>{S.placement.fitFootprint} {fmt(fits.footprint)}</button>
          <button type="button" style={SMALL_BUTTON} title={S.placement.fitHeightTitle} onClick={() => onChange({ scale: tidy(fits.height) })}>{S.placement.fitHeight} {fmt(fits.height)}</button>
          <button type="button" style={SMALL_BUTTON} title={S.placement.fitBoxTitle} onClick={() => onChange({ scale: tidy(fits.box) })}>{S.placement.fitBox} {fmt(fits.box)}</button>
        </div>
      )}

      <div style={ROW}>
        <span style={{ width: 70, fontSize: 12 }} title={S.placement.yawTitle}>{S.placement.yaw}</span>
        <NumberField value={draft.yaw} step={1} style={NUMBER} aria-label={S.placement.yaw} onCommit={(v) => onChange({ yaw: v })} />
        <input
          type="range" min={-180} max={180} step={1} value={Math.min(180, Math.max(-180, draft.yaw))} style={{ flex: 1, accentColor: ACCENT }} aria-label={`${S.placement.yaw} slider`}
          onChange={(e) => onChange({ yaw: parseFloat(e.target.value) })}
        />
      </div>
      <div style={{ ...ROW, paddingLeft: 76 }}>
        <button type="button" style={SMALL_BUTTON} onClick={() => onChange({ yaw: ((draft.yaw - 90 + 540) % 360) - 180 })}>{S.placement.minus90}</button>
        <button type="button" style={SMALL_BUTTON} onClick={() => onChange({ yaw: 0 })}>{S.placement.zero}</button>
        <button type="button" style={SMALL_BUTTON} onClick={() => onChange({ yaw: ((draft.yaw + 90 + 540) % 360) - 180 })}>{S.placement.plus90}</button>
        <label style={{ ...ROW, marginLeft: 'auto', fontSize: 12, cursor: 'pointer' }} title={S.placement.mirrorTitle}>
          <input type="checkbox" checked={draft.mirror} onChange={(e) => onChange({ mirror: e.target.checked })} style={{ accentColor: ACCENT }} />
          {S.placement.mirror}
        </label>
      </div>

      {/* Standing the model up (README step 0): tip about its left-right axis, roll about its front-back axis, before the turn. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12 }} title={S.placement.standTitle}>{S.placement.stand}</span>
        {([['tilt', S.placement.tilt, S.placement.tiltTitle], ['roll', S.placement.roll, S.placement.rollTitle]] as const).map(([key, label, title]) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }} key={key}>
            <div style={ROW}>
              <span style={{ width: 70, fontSize: 12 }} title={title}>{label}</span>
              <NumberField value={draft[key]} step={1} style={NUMBER} aria-label={label} onCommit={(v) => onChange({ [key]: v })} />
              <input
                type="range" min={-180} max={180} step={1} value={Math.min(180, Math.max(-180, draft[key]))} style={{ flex: 1, accentColor: ACCENT }} aria-label={`${label} slider`}
                onChange={(e) => onChange({ [key]: parseFloat(e.target.value) })}
              />
            </div>
            <div style={{ ...ROW, paddingLeft: 76 }}>
              <button type="button" style={SMALL_BUTTON} onClick={() => onChange({ [key]: ((draft[key] - 90 + 540) % 360) - 180 })}>{S.placement.minus90}</button>
              <button type="button" style={SMALL_BUTTON} onClick={() => onChange({ [key]: 0 })}>{S.placement.zero}</button>
              <button type="button" style={SMALL_BUTTON} onClick={() => onChange({ [key]: ((draft[key] + 90 + 540) % 360) - 180 })}>{S.placement.plus90}</button>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12 }}>{S.placement.offset}</span>
        {offsetRow(S.placement.along, 0)}
        {offsetRow(S.placement.up, 1)}
        {offsetRow(S.placement.into, 2)}
      </div>

      {/* Copies: the model repeated — a stack of crates, a row of bins (README step 6). */}
      <div style={{ display: simple ? 'none' : 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12 }} title={S.placement.copiesTitle}>{S.placement.copies}</span>
        <div style={ROW}>
          <span style={{ width: 70, fontSize: 12 }}>{S.placement.copyCount}</span>
          <NumberField
            value={draft.copies.count} step={1} min={1} max={MAX_COPIES} accept={(v) => Number.isInteger(v) && v >= 1 && v <= MAX_COPIES}
            style={NUMBER} aria-label={S.placement.copyCount} onCommit={(v) => onChange({ copies: { ...draft.copies, count: v } })}
          />
          <select
            value={draft.copies.axis} aria-label={S.placement.copyAxis} style={{ ...control, padding: '3px 6px' }}
            onChange={(e) => onChange({ copies: { ...draft.copies, axis: e.target.value as CopyAxis } })}
          >
            <option value="up">{S.placement.copyUp}</option>
            <option value="along">{S.placement.copyAlong}</option>
            <option value="into">{S.placement.copyInto}</option>
          </select>
        </div>
        <div style={ROW}>
          <span style={{ width: 70, fontSize: 12 }}>{S.placement.copyGap}</span>
          <NumberField value={draft.copies.gap} step={0.1} min={0} accept={(v) => v >= 0} style={NUMBER} aria-label={S.placement.copyGap} onCommit={(v) => onChange({ copies: { ...draft.copies, gap: tidy(v) } })} />
        </div>
      </div>

      {/* At most so many of this model on a wall (or in a room): two identical boots side by side are not wanted. */}
      {!simple && !partOf && (
        <div style={ROW} title={S.placement.perWallTitle}>
          <span style={{ width: 70, fontSize: 12 }}>{S.placement.perWall}</span>
          <NumberField
            value={draft.perWall ?? 0} step={1} min={0} max={MAX_PER_WALL} accept={(v) => Number.isInteger(v) && v >= 0 && v <= MAX_PER_WALL}
            style={NUMBER} aria-label={S.placement.perWall} onCommit={(v) => onChange({ perWall: v > 0 ? v : undefined })}
          />
          <span style={{ fontSize: 11, color: '#9a9aa6' }}>{draft.perWall ? S.placement.perWallOn(draft.perWall) : S.placement.perWallOff}</span>
        </div>
      )}

      {/* The box the designer stages the model at: the placed size, or one of the user's own (README "Staging round the real size"). */}
      {modelBounds && stagedDefault && !partOf && !simple && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ ...ROW, fontSize: 12, cursor: 'pointer' }} title={S.placement.stageBoxTitle}>
            <input
              type="checkbox" checked={!!draft.stage} style={{ accentColor: ACCENT }} aria-label={S.placement.stageBox}
              onChange={(e) => onChange({ stage: e.target.checked ? tidyBox(stagedDefault) : undefined })}
            />
            {S.placement.stageBox} {S.placement.stageBoxOwn}
          </label>
          {draft.stage && (
            <>
              <div style={ROW}>
                {([['w', S.readout.along], ['d', S.readout.deep], ['h', S.readout.tall]] as const).map(([key, label]) => (
                  <React.Fragment key={key}>
                    <span style={{ fontSize: 11, color: MUTED }}>{label}</span>
                    <NumberField
                      value={draft.stage![key]} step={0.1} min={0.1} accept={(v) => v > 0} style={{ ...NUMBER, width: 60 }} aria-label={`${S.placement.stageBox} ${label}`}
                      onCommit={(v) => onChange({ stage: { ...draft.stage!, [key]: tidy(v) } })}
                    />
                  </React.Fragment>
                ))}
                <span style={{ fontSize: 11, color: MUTED }}>{S.readout.feet}</span>
              </div>
              <div style={ROW}>
                <button type="button" style={SMALL_BUTTON} onClick={() => onChange({ stage: tidyBox(stagedDefault) })}>{S.placement.stageBoxFromPlaced}</button>
                <button type="button" style={SMALL_BUTTON} onClick={() => onChange({ stage: { w: tidy(kitExtent[0]), d: tidy(kitExtent[2]), h: tidy(kitExtent[1]) } })}>{S.placement.stageBoxFromKit}</button>
              </div>
              <span style={{ fontSize: 11, color: MUTED }}>{S.placement.stageBoxNote}</span>
            </>
          )}
        </div>
      )}

      <div style={ROW}>
        <button type="button" style={{ ...PRIMARY_BUTTON, opacity: dirty && !saving && modelBounds ? 1 : 0.45 }} disabled={!dirty || saving || !modelBounds} onClick={onSave}>
          {saving ? S.placement.saving : S.placement.save}
        </button>
        <button type="button" style={{ ...control, cursor: dirty ? 'pointer' : 'default', opacity: dirty ? 1 : 0.5 }} disabled={!dirty} onClick={onReset}>{S.placement.reset}</button>
      </div>

      <div>
        <div style={LABEL}>{S.readout.title}</div>
        <table style={{ fontSize: 12, borderCollapse: 'collapse', marginTop: 4 }}>
          <thead>
            <tr>
              <td />
              <td style={{ color: MUTED, textAlign: 'right', paddingLeft: 10 }}>{S.readout.along}</td>
              <td style={{ color: MUTED, textAlign: 'right', paddingLeft: 10 }}>{S.readout.deep}</td>
              <td style={{ color: MUTED, textAlign: 'right', paddingLeft: 10 }}>{S.readout.tall}</td>
              <td />
            </tr>
          </thead>
          <tbody>
            {sizeRow(S.readout.model, size, S.readout.metres)}
            {sizeRow(S.readout.model, size ? [size[0] / FT, size[1] / FT, size[2] / FT] : null, S.readout.feet)}
            {/* Placed and kit extents are [along (x), tall (y), deep (z)] in the prop frame; shown along / deep / tall like the rest. */}
            {sizeRow(S.readout.placed, placedExtent ? [placedExtent[0], placedExtent[2], placedExtent[1]] : null, S.readout.feet)}
            {/* What the designer stages and draws it at: the stage box when one is set, else the placed size (as tall as the model's top). */}
            {stagedDefault && sizeRow(S.readout.staged, draft.stage ? [draft.stage.w, draft.stage.d, draft.stage.h] : [stagedDefault.w, stagedDefault.d, stagedDefault.h], S.readout.feet)}
            {sizeRow(S.readout.kitBox, [kitExtent[0], kitExtent[2], kitExtent[1]], S.readout.feet)}
            {sizeRow(S.readout.difference, placedExtent ? [placedExtent[0] - kitExtent[0], placedExtent[2] - kitExtent[2], placedExtent[1] - kitExtent[1]] : null, S.readout.feet, signed)}
          </tbody>
        </table>
        {placed && (
          <p style={{ margin: '6px 0 0', fontSize: 11, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
            {S.readout.bottom} y {fmt(placed.min[1])} · {S.readout.centre} x {fmt((placed.min[0] + placed.max[0]) / 2)}, z {fmt((placed.min[2] + placed.max[2]) / 2)} {S.readout.feet}
            {' · '}x {fmt(placed.min[0])}…{fmt(placed.max[0])}, z {fmt(placed.min[2])}…{fmt(placed.max[2])}
          </p>
        )}
        {clearance !== null && (
          <div style={{ ...ROW, marginTop: 4 }}>
            <p
              className="prop-studio-wall" data-wall={behindWall ? 'behind' : 'clear'}
              style={{ margin: 0, fontSize: 12, fontVariantNumeric: 'tabular-nums', color: behindWall ? WARN : MUTED, fontWeight: behindWall ? 700 : 400 }}
            >
              {behindWall ? S.readout.behindWall(cm(-clearance), fmt(-clearance)) : S.readout.clearOfWall(cm(clearance), fmt(clearance))}
            </p>
            {/* The model's nearest point moved onto the wall line: measured on the drawn mesh when the viewer can, else the box. */}
            <button
              type="button" style={{ ...SMALL_BUTTON, marginLeft: 'auto', whiteSpace: 'nowrap' }} title={S.readout.flushTitle}
              onClick={() => { if (onFlushToWall && candidate.phase === 'ready') onFlushToWall(); else setOffset(2, draft.offset[2] - clearance); }}
            >
              {S.readout.flush}
            </button>
            {/* The model's lowest point — its mesh as drawn, tipped or rolled — set on the floor. */}
            {frame.kind === 'floor' && onRestOnFloor && (
              <button type="button" style={{ ...SMALL_BUTTON, whiteSpace: 'nowrap' }} title={S.readout.restTitle} disabled={candidate.phase !== 'ready'} onClick={onRestOnFloor}>
                {S.readout.rest}
              </button>
            )}
          </div>
        )}
        {placed && <p style={{ margin: '6px 0 0', fontSize: 11, color: MUTED }}>{S.readout.stagingNote}</p>}
        {candidate.phase === 'ready' && size && (
          <p style={{ margin: '4px 0 0', fontSize: 11, color: previewDiffers ? ACCENT : MUTED }}>
            {S.candidate.previewSize}: {S.search.dims(fmt(candidate.sizeMetres[0]), fmt(candidate.sizeMetres[1]), fmt(candidate.sizeMetres[2]))}
            {previewDiffers && ` — ${S.candidate.previewDiffers}`}
          </p>
        )}
      </div>
      </>)}
    </section>
  );
};

export default PlacementPanel;
